import type {
  Appointment,
  AppointmentBlockedCounts,
  AppointmentBlockedCountsByCreator,
  AppointmentFilter,
  AppointmentPageParams,
  AppointmentStatus,
  CreateAppointmentOutcomePayload,
  CreateAppointmentPayload,
  LinkedAppointment,
  UpdateAppointmentPayload,
} from "@/features/clinic/types/appointment";
import type { Data, OffsetPaginationMeta } from "@/types/pagination";

import { createId, createPastTimestamp } from "../../generators/base";
import { useMockStore } from "../../store";
import { CLINICSOFT_MOCK_INTEGRATION } from "../integration";
import { updateContactMock } from "../contact";

import { createAdvanceMock, createReceiptMock } from "./billing";
import { createInvoiceMock } from "./invoices";

import { EMPTY_BLOCKED_COUNTS } from "@/features/clinic/api/appointments/use-get-blocked-counts";
import {
  BLOCKED_REASON_COUNT_KEY,
  CRM_PUSH_FAILURE_WINDOW_DAYS,
  REBOOK_FOLLOW_UP_REASON,
  STILL_DECIDING_FOLLOW_UP_REASON,
  NOTHING_BOOKED_FOLLOW_UP_REASON,
  RECALL_FOLLOW_UP_REASON,
} from "@/features/clinic/constants/appointments";
import { missingAppointmentDetail } from "@/features/clinic/helpers/department";
import {
  APPOINTMENT_BOOKED_BY,
  APPOINTMENT_CRM_STATUS,
  APPOINTMENT_STATUS,
  ATTENDANCE_ANSWER,
  BOOKING_TYPE,
  CONSULTATION_ANSWER,
  NO_SHOW_NEXT_STEP,
  RESOLUTION_STATUS,
  RESOLVE_BLOCKED_REASON,
} from "@/features/clinic/types/appointment";
import { SETTLEMENT_STATUS } from "@/features/clinic/types/billing";
import { findOutcomeReasonMock } from "@/mock/handlers/outcome-reasons";

const STATUS_COLORS: Record<string, string> = {
  pending: "#3b82f6",
  confirmed: "#22c55e",
  completed: "#6b7280",
  cancelled: "#ef4444",
  no_show: "#f59e0b",
};

/**
 * Stands in for the server's claiming crons: the block a row would carry, given
 * what the crons and the reset hook would have done to it by now. A waiting
 * block is stored, not derived - it stays until an outcome answers it - so it
 * passes through untouched.
 */
function withResolveBlock(appointment: Appointment): Appointment {
  if (
    appointment.isResolveBlocked &&
    appointment.resolveBlockedReason === RESOLVE_BLOCKED_REASON.WAITING_ON_USER
  ) {
    return {
      ...appointment,
      resolutionStatus: RESOLUTION_STATUS.AWAITING_DECISION,
    };
  }

  const now = Date.now();
  const staleStatus =
    (appointment.status === "pending" || appointment.status === "confirmed") &&
    new Date(appointment.endDate).getTime() < now;
  const paymentMissing =
    appointment.status === "completed" &&
    appointment.bookingType === BOOKING_TYPE.SERVICE &&
    (appointment.value ?? null) === null;

  const settled =
    !staleStatus &&
    !paymentMissing &&
    (appointment.status === "completed" ||
      appointment.status === "no_show" ||
      appointment.status === "cancelled");

  return {
    ...appointment,
    isResolveBlocked: staleStatus || paymentMissing,
    resolveBlockedReason: staleStatus
      ? RESOLVE_BLOCKED_REASON.STALE_STATUS
      : paymentMissing
        ? RESOLVE_BLOCKED_REASON.PAYMENT_MISSING
        : null,
    resolutionStatus: staleStatus
      ? RESOLUTION_STATUS.AWAITING_ATTENDANCE
      : paymentMissing
        ? RESOLUTION_STATUS.AWAITING_PAYMENT
        : settled
          ? RESOLUTION_STATUS.SETTLED
          : RESOLUTION_STATUS.NOT_DUE,
  };
}

/**
 * Get all appointments with filtering
 */
export function getAppointmentsMock(
  _workspaceId: string,
  filters: AppointmentFilter = {},
): Appointment[] {
  const store = useMockStore.getState();
  let appointments = store.appointments.map(withResolveBlock);

  // Filter by doctorIds (array)
  if (filters.doctorIds && filters.doctorIds.length > 0) {
    appointments = appointments.filter((a) =>
      filters.doctorIds!.includes(a.doctorId),
    );
  }

  // Filter by date range
  if (filters.startDate) {
    const startDate = new Date(filters.startDate);

    appointments = appointments.filter(
      (a) => new Date(a.startDate) >= startDate,
    );
  }

  if (filters.endDate) {
    const endDate = new Date(filters.endDate);

    appointments = appointments.filter((a) => new Date(a.endDate) <= endDate);
  }

  // Filter by statuses (array)
  if (filters.statuses && filters.statuses.length > 0) {
    appointments = appointments.filter((a) =>
      filters.statuses!.includes(a.status),
    );
  }

  // Filter by contactId
  if (filters.contactId) {
    appointments = appointments.filter(
      (a) => a.contactId === filters.contactId,
    );
  }

  // Filter by booking source (team vs assistant vs CRM)
  if (filters.bookedBy && filters.bookedBy.length > 0) {
    appointments = appointments.filter(
      (a) => a.bookedBy != null && filters.bookedBy!.includes(a.bookedBy),
    );
  }

  // Filter by who booked it ("Just me" scope on the dashboard)
  if (filters.bookedByUserIds && filters.bookedByUserIds.length > 0) {
    appointments = appointments.filter(
      (a) =>
        a.bookedByUserId != null &&
        filters.bookedByUserIds!.includes(a.bookedByUserId),
    );
  }

  if (filters.blockedReasons?.length) {
    const reasons = new Set(filters.blockedReasons);
    const now = Date.now();

    // Mirrors the server: a waiting block is not a question anyone can answer
    // until the date the patient asked for has passed.
    appointments = appointments.filter(
      (a) =>
        a.isResolveBlocked &&
        a.resolveBlockedReason != null &&
        reasons.has(a.resolveBlockedReason) &&
        (a.resolveBlockedReason !== RESOLVE_BLOCKED_REASON.WAITING_ON_USER ||
          !a.waitingOnUserTill ||
          new Date(a.waitingOnUserTill).getTime() <= now),
    );
  }

  // Mirrors the server: still to come, not cancelled, and lacking a treatment
  // or any department that can be told from it.
  if (filters.missingDetails) {
    const departments = useMockStore.getState().departments;
    const now = Date.now();

    appointments = appointments.filter(
      (a) =>
        new Date(a.endDate).getTime() >= now &&
        a.status !== "cancelled" &&
        missingAppointmentDetail(a, departments) !== null,
    );
  }

  // Mirrors the server: pushed to the CRM, refused, and recent enough that
  // putting it back on the schedule still means something.
  if (filters.crmStatus === APPOINTMENT_CRM_STATUS.NOT_SYNCED) {
    const since =
      Date.now() - CRM_PUSH_FAILURE_WINDOW_DAYS * 24 * 60 * 60 * 1000;

    appointments = appointments.filter(
      (a) =>
        !a.externalId &&
        !!a.externalData?.error &&
        a.status !== "cancelled" &&
        new Date(a.startDate).getTime() >= since,
    );
  }

  if (filters.crmStatus === APPOINTMENT_CRM_STATUS.SYNCED) {
    appointments = appointments.filter((a) => !!a.externalId);
  }

  return appointments.sort(
    (a, b) => new Date(a.startDate).getTime() - new Date(b.startDate).getTime(),
  );
}

/** The server's page order: newest visit, then newest booking, then id. */
const byNewestVisit = (a: Appointment, b: Appointment) =>
  new Date(b.startDate).getTime() - new Date(a.startDate).getTime() ||
  new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime() ||
  b.id.localeCompare(a.id);

export function getAppointmentsPageMock(
  workspaceId: string,
  filters: AppointmentFilter,
  { page, limit }: AppointmentPageParams,
): Data<Appointment, OffsetPaginationMeta> {
  const matching = getAppointmentsMock(workspaceId, filters).sort(
    byNewestVisit,
  );
  const start = (page - 1) * limit;

  return {
    result: matching.slice(start, start + limit),
    meta: {
      count: matching.length,
      currentPage: page,
      previousPage: page > 1 ? page - 1 : null,
      nextPage: start + limit < matching.length ? page + 1 : null,
    },
  };
}

const toLinkedAppointment = (appointment: Appointment): LinkedAppointment => ({
  id: appointment.id,
  startDate: appointment.startDate,
  status: appointment.status,
  bookingType: appointment.bookingType,
  treatment: appointment.treatment ?? null,
  value: appointment.value ?? null,
});

/**
 * Get a single appointment by ID.
 *
 * Resolves both ends of the consultation-to-service link the way the detail
 * endpoint does - off the foreign key rather than from fixed fixtures, so
 * booking a service from a consultation in mock mode links up on its own.
 */
export function getAppointmentMock(
  appointmentId: string,
): Appointment | undefined {
  const store = useMockStore.getState();
  const appointment = store.appointments.find((a) => a.id === appointmentId);

  if (!appointment) return undefined;

  const now = Date.now();
  const nextAppointment = appointment.contactId
    ? (store.appointments
        .filter(
          (a) =>
            a.id !== appointmentId &&
            a.contactId === appointment.contactId &&
            (a.status === APPOINTMENT_STATUS.PENDING ||
              a.status === APPOINTMENT_STATUS.CONFIRMED) &&
            new Date(a.startDate).getTime() > now,
        )
        .sort(
          (a, b) =>
            new Date(a.startDate).getTime() - new Date(b.startDate).getTime(),
        )
        .map(toLinkedAppointment)[0] ?? null)
    : null;

  return {
    ...withResolveBlock(appointment),
    originAppointment: appointment.originAppointmentId
      ? (store.appointments
          .filter((a) => a.id === appointment.originAppointmentId)
          .map(toLinkedAppointment)[0] ?? null)
      : null,
    convertedAppointments: store.appointments
      .filter((a) => a.originAppointmentId === appointmentId)
      .map(toLinkedAppointment),
    nextAppointment,
  };
}

/**
 * Create a new appointment
 */
export function createAppointmentMock(
  payload: CreateAppointmentPayload,
): Appointment {
  const store = useMockStore.getState();
  const timestamp = createPastTimestamp(0);

  // Get doctor info
  const doctor = store.doctors.find((d) => d.id === payload.doctorId);

  // Get contact info if provided
  const contact = payload.contactId
    ? store.contacts.find((c) => c.id === payload.contactId)
    : null;

  // Get treatment info if provided
  const treatment = payload.treatmentId
    ? store.treatments.find((t) => t.id === payload.treatmentId)
    : null;

  const status = payload.status ?? "pending";

  // Mirrors the server: a booking starts Unconfirmed or Confirmed.
  if (
    status !== APPOINTMENT_STATUS.PENDING &&
    status !== APPOINTMENT_STATUS.CONFIRMED
  ) {
    throw new Error(
      "A new appointment can only be Unconfirmed or Confirmed. Change its status after it is created.",
    );
  }

  const newAppointment: Appointment = {
    id: createId(),
    startDate: payload.startDate,
    endDate: payload.endDate,
    doctorId: payload.doctorId,
    doctor: doctor
      ? {
          id: doctor.id,
          name: doctor.name,
          photo: doctor.photo,
          externalId: doctor.externalId ?? null,
        }
      : {
          id: payload.doctorId,
          name: "Unknown",
          photo: null,
          externalId: null,
        },
    contactId: payload.contactId ?? null,
    contact: contact
      ? {
          id: contact.id,
          firstName: contact.firstName,
          lastName: contact.lastName,
          phone: contact.phone,
          email: contact.email,
          imageUrl: contact.imageUrl,
          assignedUserId: contact.assignedUserId ?? null,
          origin: contact.origin ?? null,
        }
      : undefined,
    treatmentId: payload.treatmentId ?? null,
    treatment: treatment ? { id: treatment.id, name: treatment.name } : null,
    departmentId: payload.departmentId ?? null,
    bookedByUserId: payload.bookedByUserId ?? null,
    bookedBy: payload.bookedByUserId ? APPOINTMENT_BOOKED_BY.USER : undefined,
    patients: payload.patients ?? [],
    status,
    notes: payload.notes ?? null,
    visitType: payload.visitType ?? null,
    // Mirrors the API's default: only whoever books it can say otherwise.
    bookingType: payload.bookingType ?? BOOKING_TYPE.SERVICE,
    isPaid: payload.isPaid ?? null,
    outcome: null,
    isResolveBlocked: false,
    resolveBlockedReason: null,
    resolutionStatus: RESOLUTION_STATUS.NOT_DUE,
    originAppointmentId: payload.originAppointmentId ?? null,
    color: STATUS_COLORS[status],
    organizationId: store.organization?.id ?? "",
    workspaceId: store.workspaces[0]?.id ?? "",
    createdAt: timestamp,
    updatedAt: timestamp,
  };

  store.addAppointment(newAppointment);

  return newAppointment;
}

/**
 * Update an existing appointment
 */
export function updateAppointmentMock(
  appointmentId: string,
  payload: UpdateAppointmentPayload,
): Appointment | undefined {
  const store = useMockStore.getState();
  const appointment = store.appointments.find((a) => a.id === appointmentId);

  if (!appointment) return undefined;

  // Get updated references if IDs changed
  let doctor = appointment.doctor;

  if (payload.doctorId && payload.doctorId !== appointment.doctorId) {
    const newDoctor = store.doctors.find((d) => d.id === payload.doctorId);

    doctor = newDoctor
      ? {
          id: newDoctor.id,
          name: newDoctor.name,
          photo: newDoctor.photo,
          externalId: newDoctor.externalId ?? null,
        }
      : {
          id: payload.doctorId,
          name: "Unknown",
          photo: null,
          externalId: null,
        };
  }

  let contact: typeof appointment.contact = appointment.contact;

  if (payload.contactId !== undefined) {
    if (payload.contactId === null) {
      contact = undefined;
    } else if (payload.contactId !== appointment.contactId) {
      const newContact = store.contacts.find((c) => c.id === payload.contactId);

      contact = newContact
        ? {
            id: newContact.id,
            firstName: newContact.firstName,
            lastName: newContact.lastName,
            phone: newContact.phone,
            email: newContact.email,
            imageUrl: newContact.imageUrl,
            assignedUserId: newContact.assignedUserId ?? null,
            origin: newContact.origin ?? null,
          }
        : undefined;
    }
  }

  let treatment = appointment.treatment;

  if (payload.treatmentId !== undefined) {
    if (payload.treatmentId === null) {
      treatment = null;
    } else if (payload.treatmentId !== appointment.treatmentId) {
      const newTreatment = store.treatments.find(
        (t) => t.id === payload.treatmentId,
      );

      treatment = newTreatment
        ? { id: newTreatment.id, name: newTreatment.name }
        : null;
    }
  }

  const status = payload.status ?? appointment.status;

  const updates: Partial<Appointment> = {
    ...payload,
    doctor,
    contact,
    treatment,
    patients: payload.patients ?? appointment.patients,
    color: STATUS_COLORS[status],
    updatedAt: new Date().toISOString(),
  };

  if (payload.bookedByUserId !== undefined) {
    updates.bookedBy = payload.bookedByUserId
      ? APPOINTMENT_BOOKED_BY.USER
      : appointment.bookedBy === APPOINTMENT_BOOKED_BY.USER
        ? undefined
        : appointment.bookedBy;
  }

  store.updateAppointment(appointmentId, updates);

  return { ...appointment, ...updates };
}

/**
 * Bulk update the status of multiple appointments.
 */
export function bulkUpdateAppointmentStatusMock(
  ids: string[],
  status: AppointmentStatus,
): { updated: number } {
  const store = useMockStore.getState();
  const timestamp = new Date().toISOString();
  let updated = 0;

  for (const id of ids) {
    const exists = store.appointments.some((a) => a.id === id);

    if (exists) {
      store.updateAppointment(id, {
        status,
        color: STATUS_COLORS[status],
        updatedAt: timestamp,
      });
      updated += 1;
    }
  }

  return { updated };
}

/**
 * Delete an appointment
 */
export function deleteAppointmentMock(appointmentId: string): boolean {
  const store = useMockStore.getState();
  const exists = store.appointments.some((a) => a.id === appointmentId);

  if (exists) {
    store.deleteAppointment(appointmentId);

    return true;
  }

  return false;
}

/** Writes the answered row back with its resolve block lifted. Kept apart from
 *  `updateAppointmentMock` because the block is not a field the update endpoint
 *  takes - the API derives it from what the update was. */
function settleMockBlock(
  appointment: Appointment | undefined,
): Appointment | undefined {
  if (!appointment) return undefined;

  const settled: Appointment = {
    ...appointment,
    isResolveBlocked: false,
    resolveBlockedReason: null,
    waitingOnUserTill: null,
    resolutionStatus: RESOLUTION_STATUS.SETTLED,
  };

  useMockStore.getState().updateAppointment(appointment.id, settled);

  return settled;
}

/** An upcoming booking answers the next-visit question: point it back at this one. */
function linkFollowOn(nextAppointmentId: string, originAppointmentId: string) {
  const store = useMockStore.getState();
  const next = store.appointments.find((a) => a.id === nextAppointmentId);

  if (next && !next.originAppointmentId) {
    store.updateAppointment(nextAppointmentId, { originAppointmentId });
  }
}

/**
 * Applies an outcome the way the API does - the consultation settles, and a
 * service booked on the spot is created pointing back at it.
 *
 * The linked appointment is what the funnel report reads to credit revenue, so
 * the mock has to create it or the reporting screens have nothing to show.
 */
export function resolveAppointmentOutcomeMock(
  appointmentId: string,
  payload: CreateAppointmentOutcomePayload,
): Appointment | undefined {
  const store = useMockStore.getState();
  const appointment = store.appointments.find((a) => a.id === appointmentId);

  if (!appointment) return undefined;

  const waitingForPatient =
    payload.attendance === ATTENDANCE_ANSWER.NO_SHOW
      ? payload.noShowNext === NO_SHOW_NEXT_STEP.WILL_TELL_LATER
      : payload.consultationAnswer === CONSULTATION_ANSWER.STILL_DECIDING;
  const reminderTime = new Date(payload.waitingOnUserTill ?? "").getTime();

  if (
    waitingForPatient &&
    (!Number.isFinite(reminderTime) || reminderTime <= Date.now())
  ) {
    throw new Error("Choose a future reminder time");
  }

  if (payload.departmentId || payload.treatmentId) {
    updateAppointmentMock(appointmentId, {
      ...(payload.departmentId ? { departmentId: payload.departmentId } : {}),
      ...(payload.treatmentId ? { treatmentId: payload.treatmentId } : {}),
    });
  }

  // Every no-show answer settles the block and none reaches the branch below
  // that writes one, so the block has to be cleared here - otherwise the mocked
  // row stays blocked after it has been answered and every surface that asks
  // off `isResolveBlocked` goes on asking a question the agent just answered.
  //
  // All three leave the row `no_show`: the missed slot stays on the record
  // whatever happens next, which is the whole point of asking.
  if (payload.attendance === ATTENDANCE_ANSWER.NO_SHOW) {
    const missed = updateAppointmentMock(appointmentId, { status: "no_show" });

    if (!missed) return undefined;

    // They will name a date. The row stays blocked on the same waiting step a
    // still-deciding consultation uses - only the status tells the two apart.
    if (payload.noShowNext === NO_SHOW_NEXT_STEP.WILL_TELL_LATER) {
      const waiting: Appointment = {
        ...missed,
        isResolveBlocked: true,
        resolveBlockedReason: RESOLVE_BLOCKED_REASON.WAITING_ON_USER,
        resolutionStatus: RESOLUTION_STATUS.AWAITING_DECISION,
        waitingOnUserTill: payload.waitingOnUserTill ?? null,
      };

      store.updateAppointment(appointmentId, waiting);
      if (appointment.contactId) {
        updateContactMock(appointment.contactId, {
          id: appointment.contactId,
          status: "closed",
          followUp: {
            dueAt: payload.waitingOnUserTill!,
            reason: payload.followUpReason || REBOOK_FOLLOW_UP_REASON,
          },
        });
      }

      return waiting;
    }

    if (
      payload.noShowNext === NO_SHOW_NEXT_STEP.RESCHEDULED &&
      payload.nextAppointmentId
    ) {
      linkFollowOn(payload.nextAppointmentId, appointmentId);
    } else if (
      // The new slot is its own appointment pointing back at the one they
      // missed, not a move of it.
      payload.noShowNext === NO_SHOW_NEXT_STEP.RESCHEDULED &&
      payload.startDate &&
      payload.endDate
    ) {
      const rebooked = createAppointmentMock({
        doctorId: appointment.doctorId,
        treatmentId: appointment.treatmentId,
        contactId: appointment.contactId,
        patients: appointment.patients ?? [],
        startDate: payload.startDate,
        endDate: payload.endDate,
        status: "pending",
        notes: null,
        cancellationReason: null,
      } as CreateAppointmentPayload);

      store.updateAppointment(rebooked.id, {
        ...rebooked,
        bookingType: appointment.bookingType,
        isPaid: null,
        originAppointmentId: appointmentId,
      });
    }

    return settleMockBlock(missed);
  }

  const contactId = appointment.contactId ?? store.contacts[0]?.id ?? "";
  const latestAdvanceId =
    store.advances.find(
      (advance) =>
        advance.contactId === appointment.contactId &&
        advance.status === SETTLEMENT_STATUS.CLEARED,
    )?.id ?? null;

  // The paid visit writes the invoice and the receipt that cleared it; a draw
  // on the advance writes only the allocation receipt; a new advance is banked
  // on the contact with this visit as the one it was paid at.
  if (payload.payment) {
    const created = createInvoiceMock({
      appointmentId,
      total: payload.payment.amount,
      notes: payload.payment.notes,
      confirmDuplicate: true,
    });

    createReceiptMock(contactId, {
      appointmentId,
      invoiceId: created.status === "saved" ? created.invoice.id : null,
      amount: payload.payment.amount,
      settledFromAdvance: false,
    });
  }
  if (payload.advanceDraw) {
    if (!latestAdvanceId) throw new Error("No advance on file");

    createReceiptMock(contactId, {
      appointmentId,
      amount: payload.advanceDraw.amount,
      settledFromAdvance: true,
    });
  }
  if (payload.newAdvance) {
    createAdvanceMock(contactId, {
      appointmentId,
      amount: payload.newAdvance.amount,
    });
  }

  const settledPaid = payload.advanceDraw || payload.newAdvance;
  const settled = updateAppointmentMock(appointmentId, {
    status: "completed",
    ...(settledPaid
      ? { isPaid: true }
      : payload.isPaid !== undefined
        ? { isPaid: payload.isPaid }
        : {}),
  });

  if (!settled) return undefined;

  // The advance the visit drew from is remembered; with a CRM its figure
  // arrives with the allocation receipt, so nothing is priced here.
  const advanceId =
    payload.usedAdvance || payload.advanceDraw ? latestAdvanceId : null;

  const stillDeciding =
    payload.consultationAnswer === CONSULTATION_ANSWER.STILL_DECIDING;

  const pickedReason = payload.outcomeReasonId
    ? findOutcomeReasonMock(payload.outcomeReasonId)
    : undefined;
  const withOutcome: Appointment = {
    ...settled,
    advanceId,
    outcome:
      payload.consultationAnswer &&
      payload.consultationAnswer !== CONSULTATION_ANSWER.STILL_DECIDING
        ? payload.consultationAnswer
        : null,
    outcomeReason: pickedReason
      ? { id: pickedReason.id, label: pickedReason.label }
      : null,
    isResolveBlocked: stillDeciding,
    resolveBlockedReason: stillDeciding
      ? RESOLVE_BLOCKED_REASON.WAITING_ON_USER
      : null,
    resolutionStatus: stillDeciding
      ? RESOLUTION_STATUS.AWAITING_DECISION
      : RESOLUTION_STATUS.SETTLED,
    waitingOnUserTill: stillDeciding
      ? (payload.waitingOnUserTill ?? null)
      : null,
  };

  store.updateAppointment(appointmentId, withOutcome);
  if (stillDeciding && appointment.contactId) {
    updateContactMock(appointment.contactId, {
      id: appointment.contactId,
      status: "closed",
      followUp: {
        dueAt: payload.waitingOnUserTill!,
        reason: payload.followUpReason || STILL_DECIDING_FOLLOW_UP_REASON,
      },
    });
  }

  if (payload.followUpAt && appointment.contactId) {
    const defaultReason =
      payload.consultationAnswer === CONSULTATION_ANSWER.ALL_DONE
        ? RECALL_FOLLOW_UP_REASON
        : NOTHING_BOOKED_FOLLOW_UP_REASON;

    updateContactMock(appointment.contactId, {
      id: appointment.contactId,
      followUp: {
        dueAt: payload.followUpAt,
        reason: payload.followUpReason || defaultReason,
      },
    });
  }

  if (payload.nextAppointmentId) {
    linkFollowOn(payload.nextAppointmentId, appointmentId);
  } else if (payload.serviceAppointment) {
    const created = createAppointmentMock({
      ...payload.serviceAppointment,
      contactId: appointment.contactId,
      patients: appointment.patients ?? [],
      status: "confirmed",
      notes: payload.serviceAppointment.notes ?? null,
      cancellationReason: null,
    } as CreateAppointmentPayload);

    store.updateAppointment(created.id, {
      ...created,
      bookingType:
        payload.serviceAppointment.bookingType ?? BOOKING_TYPE.SERVICE,
      originAppointmentId: appointmentId,
    });
  }

  return withOutcome;
}

/**
 * Counted off the same list `getAppointmentsMock` returns, so a mock badge can
 * never disagree with the mock list it opens.
 */
export function getBlockedCountsMock(
  workspaceId: string,
): AppointmentBlockedCountsByCreator {
  const blocked = getAppointmentsMock(workspaceId, {
    blockedReasons: Object.values(RESOLVE_BLOCKED_REASON),
  });
  const missing = getAppointmentsMock(workspaceId, { missingDetails: true });
  const currentUserId = useMockStore.getState().currentUserId;

  const counts: AppointmentBlockedCountsByCreator = {
    ...EMPTY_BLOCKED_COUNTS,
    mine: { ...EMPTY_BLOCKED_COUNTS.mine },
    ai: { ...EMPTY_BLOCKED_COUNTS.ai },
  };

  const tally = (
    appointment: Appointment,
    key: keyof AppointmentBlockedCounts,
  ) => {
    counts[key]++;
    if (currentUserId && appointment.bookedByUserId === currentUserId) {
      counts.mine[key]++;
    }
    if (appointment.bookedBy === APPOINTMENT_BOOKED_BY.AI) counts.ai[key]++;
  };

  for (const appointment of blocked) {
    const reason = appointment.resolveBlockedReason;

    if (reason) tally(appointment, BLOCKED_REASON_COUNT_KEY[reason]);
  }
  for (const appointment of missing) tally(appointment, "missingDetails");
  for (const appointment of getAppointmentsMock(workspaceId, {
    crmStatus: APPOINTMENT_CRM_STATUS.NOT_SYNCED,
  })) {
    tally(appointment, "crmPushFailed");
  }

  return counts;
}

/**
 * The CRM round-trips, in miniature. Linking stamps a visit id and clears the
 * last error; a sync on a visit whose last push failed keeps failing, because
 * that is the state the desk needs to see; a sync on a drifted visit takes the
 * CRM's word and clears the drift; cancelling on the CRM drops the link.
 */
export function linkAppointmentToCrmMock(
  appointmentId: string,
): Appointment | undefined {
  const store = useMockStore.getState();
  const index = store.appointments.findIndex((a) => a.id === appointmentId);

  if (index === -1) return undefined;

  // A push the CRM already rejected is rejected again: nothing changed on
  // either side, so retrying shows the desk the same answer.
  const lastError = store.appointments[index].externalData?.error;

  if (lastError) throw new Error(lastError.details ?? lastError.message);

  const visitId = String(50000 + index);

  store.updateAppointment(appointmentId, {
    externalId: visitId,
    externalData: {
      appointmentId: visitId,
      integrationId: CLINICSOFT_MOCK_INTEGRATION.id,
      error: null,
      drift: null,
      isDrifted: false,
    },
    updatedAt: new Date().toISOString(),
  });

  return useMockStore
    .getState()
    .appointments.find((a) => a.id === appointmentId);
}

export function syncAppointmentFromCrmMock(
  appointmentId: string,
): Record<string, unknown> {
  const store = useMockStore.getState();
  const appointment = store.appointments.find((a) => a.id === appointmentId);

  if (!appointment) throw new Error("Appointment not found");
  if (appointment.externalData?.error) {
    throw new Error(
      `${CLINICSOFT_MOCK_INTEGRATION.name} is not responding. Try again in a minute.`,
    );
  }

  store.updateAppointment(appointmentId, {
    externalData: {
      ...appointment.externalData,
      drift: null,
      isDrifted: false,
    },
    updatedAt: new Date().toISOString(),
  });

  const result: Record<string, unknown> = {
    ...useMockStore.getState().appointments.find((a) => a.id === appointmentId),
  };

  return result;
}

export function cancelAppointmentOnCrmMock(
  appointmentId: string,
): Appointment | undefined {
  const store = useMockStore.getState();

  if (!store.appointments.some((a) => a.id === appointmentId)) return undefined;

  store.updateAppointment(appointmentId, {
    status: "cancelled",
    externalId: null,
    externalData: null,
    updatedAt: new Date().toISOString(),
  });

  return useMockStore
    .getState()
    .appointments.find((a) => a.id === appointmentId);
}
